import React, { useState, useRef } from "react";
import ResourceContactDetail from "./ResourceContactDetail";
import CopyTextButton from "./CopyTextButton";
import { AnimatePresence, motion } from "framer-motion";

export default function Resource({
  resourceObject,
  userList,
  handleUserListUpdate,
  handlePrint,
  isMobile,
}) {
  const isDev = window.location.hostname.includes("localhost");
  const accordionRef = useRef(null);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const {
    contactDetails,
    description,
    id,
    lat_lng,
    name_primary,
    related,
    type,
    slug,
    taxonomy,
    agency,
  } = resourceObject.item;
  const isAgency = type === "Agency";
  const websites = contactDetails.filter((detail) => detail.contact.type === "Website");
  let physicalAddresses = contactDetails.filter(
    (detail) => detail.contact.type === "PhysicalLocation"
  );

  if (resourceObject.distance) {
    physicalAddresses = physicalAddresses.map((address) => {
      return {
        ...address,
        distance: resourceObject.distance,
        bcWide: resourceObject.item.bcWide,
      };
    });
  }
  const postalAddresses = contactDetails.filter(
    (detail) => detail.contact.type === "PostalAddress"
  );
  const phoneNumbers = contactDetails.filter(
    (detail) => detail.contact.type === "PhoneNumber" && detail.contact.label !== "Fax"
  );
  const faxNumbers = contactDetails.filter(
    (detail) => detail.contact.type === "PhoneNumber" && detail.contact.label === "Fax"
  );
  const emailAddresses = contactDetails.filter((detail) => detail.contact.type === "EmailAddress");
  let plainText = name_primary + "\n" + description;
  const descriptionLines = description.split(/[.!?]/);
  const descriptionShort = { __html: descriptionLines[0] + "." };
  const descriptionLong = { __html: descriptionLines.slice(1).join(".") };

  contactDetails.forEach((detail) => {
    let contact = detail.contact;
    if (contact.type === "PhoneNumber") {
      plainText += "\n" + contact.label + ": " + contact.number;
    } else if (contact.type === "EmailAddress") {
      plainText += "\n" + contact.label + ": " + contact.address;
    } else if (contact.type === "PostalAddress" || contact.type === "PhysicalLocation") {
      let fullAddress =
        contact.line1 && contact.line1.indexOf("n/a") == -1
          ? `${contact.line1}, ${contact.line2 ? contact.line2 + "," : ""} ${contact.city}, ${
              contact.stateProvince
            } ${contact.zipPostalCode}`
          : `${contact.city}, ${contact.stateProvince}`;
      let addressLabel = contact.type === "PostalAddress" ? "Mailing Address" : "Physical Address";
      plainText += "\n" + addressLabel + ": " + fullAddress;
    }
  });

  const isAddedToList = (resourceId) => {
    return Array.isArray(userList) && userList.includes(resourceId);
  };

  const showMap = () => {
    return (
      physicalAddresses.length > 0 &&
      physicalAddresses[0].contact.line1.indexOf("n/a") === -1 &&
      lat_lng &&
      lat_lng !== "0,0"
    );
  };

  const handleShare = (id) => {
    const shareUrl = `${window.location.origin}/result/${slug}-${id}`;
    const shareData = {
      title: "Resource from bc.211.ca",
      text: "Check out this resource on BC211: " + name_primary + " (" + shareUrl + ")",
      url: shareUrl,
    };
    if (!isDev) {
      gtag("event", "share", {});
    }
    navigator.share(shareData);
  };

  const activeRelated = related
    .filter((service) => service.status === "Active")
    .sort((a, b) => a.name.localeCompare(b.name));
  const trimmedServices = isAgency && activeRelated.length > 0 ? activeRelated.slice(0, 10) : [];

  const createSlug = (name) => {
    return name
      .trim()
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const serviceUrl = `/${isAgency ? "agency-details" : "result"}/${slug}-${id}`;
  const updateUrl = `/update/?id=${id}`;

  const toggleAccordion = () => {
    setAccordionOpen(!accordionOpen);
    if (
      accordionOpen &&
      accordionRef.current &&
      accordionRef.current.getBoundingClientRect().top < 0
    ) {
      accordionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      if (!isDev) {
        gtag("event", "page_view", { page_location: serviceUrl });
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, translateY: "30px" }}
        animate={{ opacity: 1, translateY: 0 }}
        exit={{ opacity: 0, translateY: "30px" }}
      >
        <article className={`resultCard`} ref={accordionRef}>
          <div className="resultCard-header grid">
            <h3 className="h2 col-left" onClick={toggleAccordion}>
              {name_primary}
            </h3>

            {!isMobile && (
              <div className="col-right text-right">
                <button
                  onClick={() => handleUserListUpdate(id)}
                  className={`button button--secondary button--s ${
                    isAddedToList(id) ? "button--added" : ""
                  }`}
                >
                  {isAddedToList(id) ? "Remove from " : "Add to "} List
                </button>
              </div>
            )}

            <div className="col-span-6">
              {!isAgency && agency && (
                <p className="resultCard-header-info">
                  <span className="h5">
                    Provided by{" "}
                    <a href={`/agency-details/${agency.agency_slug}-${agency.agency_id}`}>
                      {agency.agency_name_primary}
                    </a>
                  </span>
                  <br />
                  {agency.agency_program_count > 1 && (
                    <span className="help-text">
                      {agency.agency_program_count - 1} other services offered by this agency
                    </span>
                  )}
                </p>
              )}
              {isAgency && activeRelated && activeRelated.length > 0 && (
                <p className="help-text">
                  {activeRelated.length} service{activeRelated.length > 1 ? "s" : ""} offered by
                  this service provider
                </p>
              )}
              <p className="p resultCard-description" dangerouslySetInnerHTML={descriptionShort} />
              <div
                className="p resultCard-description full"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>

            <div className="col-span-6 print-grid">
              {websites && websites.length > 0 && (
                <ResourceContactDetail header="Website" contactDetails={websites} />
              )}
              {physicalAddresses.length > 0 ? (
                <ResourceContactDetail header="Address" contactDetails={physicalAddresses} />
              ) : (
                <>
                  <h4>Address</h4>
                  No address available
                  {resourceObject.item.bcWide && (
                    <div className="resultCard-contact-bcwide">
                      <span className="italic">Serves all of BC</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={`accordion resultCard-body ${accordionOpen ? "accordion--open" : ""}`}>
            <div className="accordion-header">
              <button
                className="button button--s accordion-toggle"
                aria-controls={`description-${id}`}
                aria-expanded={accordionOpen}
                onClick={toggleAccordion}
              >
                {accordionOpen ? "Hide" : "Show"} Details
              </button>
            </div>
            <motion.div
              // Copy pasted from: https://www.joshuawootonn.com/how-to-animate-width-and-height-with-framer-motion
              initial={{
                height: 0,
              }}
              animate={{
                height: "auto",
                transition: {
                  height: {
                    duration: 0.4,
                  },
                },
              }}
              exit={{
                height: 0,
                transition: {
                  height: {
                    duration: 0.4,
                  },
                },
              }}
              className="accordion-body"
              id={`description-${id}`}
              hidden={!accordionOpen}
            >
              <div className="accordion-body-content grid">
                <div className="col-span-6">
                  <p
                    className="resultCard-description"
                    dangerouslySetInnerHTML={descriptionLong}
                  ></p>
                  {!isMobile && (
                    <div className="resultCard-detail-actions">
                      <CopyTextButton text={plainText} />
                      <button
                        className="button button--secondary button--s"
                        onClick={() => handlePrint(id)}
                      >
                        Print
                      </button>
                      {navigator.canShare && (
                        <button
                          className="button button--secondary button--s"
                          onClick={() => handleShare(id)}
                        >
                          Share
                        </button>
                      )}
                      <a
                        href={serviceUrl}
                        className="button button--secondary button--s"
                        target="_blank"
                        >
                        Open in New Tab
                      </a>
                      <a
                        href={updateUrl}
                        className="button button--secondary button--s icon-edit"
                      >
                        Submit an Update
                      </a>
                    </div>
                  )}

                  {accordionOpen && showMap() && (
                    <div className="map">
                      <div className="map-inner">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${lat_lng}`}
                          target="_blank"
                        >
                          <img
                            src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat_lng}&zoom=15&size=800x360&markers=${lat_lng}&key=${process.env.GOOGLE_MAPS_API_KEY}`}
                          />
                        </a>
                      </div>
                      <div>
                        <a
                          className="button button--secondary button--s"
                          href={`https://www.google.com/maps/search/?api=1&query=${lat_lng}`}
                          target="_blank"
                        >
                          <span>Open in&nbsp;</span>Google Maps
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-span-6 col-right print-grid">
                  {postalAddresses.length > 0 ? (
                    <ResourceContactDetail
                      header="Mailing address"
                      contactDetails={postalAddresses}
                    />
                  ) : (
                    <>
                      <h4>Address</h4>
                      No address available
                    </>
                  )}
                  {(emailAddresses.length > 0 ||
                    faxNumbers.length > 0 ||
                    phoneNumbers.length > 0) && (
                    <>
                      <h4>Contact</h4>
                      <ul className="resultCard-contact">
                        {emailAddresses.length > 0 && (
                          <ResourceContactDetail contactDetails={emailAddresses} />
                        )}
                        {phoneNumbers.length > 0 && (
                          <ResourceContactDetail contactDetails={phoneNumbers} />
                        )}
                        {faxNumbers.length > 0 && (
                          <ResourceContactDetail contactDetails={faxNumbers} />
                        )}
                      </ul>
                    </>
                  )}
                  {isAgency && activeRelated.length > 0 && trimmedServices.length > 0 && (
                    <>
                      <h4 className="resultCard-services">
                        Services offered by this service provider:
                      </h4>
                      <ul>
                        {trimmedServices.map((service) => {
                          const serviceSlug = createSlug(service.name);
                          return (
                            <li key={service.id}>
                              <a href={`/result/${serviceSlug}-${service.id}`}>{service.name}</a>
                            </li>
                          );
                        })}
                      </ul>
                      {activeRelated.length > 10 && (
                        <a href={`/agency-details/${slug}-${id}/#services`}>
                          See {activeRelated.length - 10} more...
                        </a>
                      )}
                    </>
                  )}
                </div>
                {isMobile && (
                  <div className="resultCard-detail-actions">
                    <CopyTextButton text={plainText} />
                    <button
                      className="button button--secondary button--s"
                      onClick={() => handlePrint(id)}
                      data-print=""
                    >
                      Print
                    </button>
                    {navigator.canShare && (
                      <button
                        className="button button--secondary button--s"
                        onClick={() => handleShare(id)}
                      >
                        Share
                      </button>
                    )}
                    <button
                      onClick={() => handleUserListUpdate(id)}
                      className={`button button--secondary button--s ${
                        isAddedToList(id) ? "button--added" : ""
                      } icon-star`}
                    >
                      {isAddedToList(id) ? "Remove from " : "Add to "} List
                    </button>
                    <a
                      href={serviceUrl}
                      className="button button--secondary button--s"
                      target="_blank"
                    >
                      Open in New Tab
                    </a>
                    <a
                        href={updateUrl}
                        className="button button--secondary button--s icon-edit"
                      >
                        Submit an Update
                      </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </article>
      </motion.div>
    </AnimatePresence>
  );
}
